<script lang="ts" setup>
import type { TreeTableFilterMeta, TreeTableFilterMetaData } from 'primevue'
import { FilterMatchMode } from '@primevue/core/api'

const confirm = useConfirm()
const emits = defineEmits<{
  open: [notification: components['schemas']['Notification']]
  'read-all': []
}>()
const filters = ref<TreeTableFilterMeta>({
  status: { value: 'delivered', matchMode: FilterMatchMode.EQUALS },
})
const toast = useToast()

const statusSet = [
  { label: 'All', value: null },
  { label: 'Unread', value: 'delivered' },
  { label: 'Read', value: 'opened' },
]
const fetchQuery = computed(() => ({ ...getFiltersValue(filters.value) }))

const {
  data: objectList,
  status,
  refresh,
} = useApi<components['schemas']['PaginatedNotificationList']>(
  '/notifications/',
  {
    params: fetchQuery,
    watch: [filters],
  }
)

const onMarkAllAsRead = async () => {
  status.value = 'pending'

  const { $api } = useNuxtApp()
  await $api('/notifications/mark-all-as-read/', {
    method: 'POST',
  })
    .then(() => {
      refresh()
    })
    .catch((error) => {
      processFormErrors(error, toast)
      emits('read-all')
    })
    .finally(() => (status.value = 'idle'))
}

const onMarkAsRead = async (object: components['schemas']['Notification']) => {
  status.value = 'pending'

  const { $api } = useNuxtApp()
  await $api(`/notifications/${object.id}/mark-as-read/`, {
    method: 'POST',
  })
    .then(() => {
      refresh()
    })
    .finally(() => (status.value = 'idle'))
}

const onOpenNotification = async (
  object: components['schemas']['Notification']
) => {
  if (object.status !== 'opened') await onMarkAsRead(object)
  emits('open', object)
}

const promptMarkAllAsRead = () => {
  confirm.require({
    accept: onMarkAllAsRead,
    acceptProps: {
      icon: 'pi pi-eye',
      label: 'Mark all as read',
      severity: 'info',
    },
    message: 'Are you sure you want to proceed?',
    rejectProps: {
      icon: 'pi pi-xmark',
      label: 'Cancel',
      outlined: true,
      severity: 'secondary',
    },
    header: 'Mark all as read',
  })
}
</script>

<template>
  <div class="space-y-3">
    <div class="flex items-center justify-end">
      <div class="overlay-caption grow" data-scene="messages">Notifications</div>

      <div>
        <a href="#" class="text-xs" @click="promptMarkAllAsRead"
          >Mark all as read</a
        >
      </div>
    </div>

    <div class="flex justify-center">
      <SelectButton
        v-model="(filters.status as TreeTableFilterMetaData).value"
        :options="statusSet"
        option-label="label"
        option-value="value"
      />
    </div>

    <div v-if="status === 'pending'" class="flex justify-center py-10">
      <ProgressSpinner />
    </div>

    <div class="overflow-y-auto space-y-5 max-h-96">
      <div
        v-for="object in objectList?.results"
        :key="object.id"
        :class="{
          'cursor-pointer': !!object.url,
        }"
        class="space-y-2"
        @click="() => onOpenNotification(object)"
      >
        <div class="flex items-center">
          <div class="grow font-semibold">{{ object.subject }}</div>
          <div class="flex items-center gap-2 text-xs">
            {{ formatDateAgo(object.created_at) }}
            <div
              v-if="object.status !== 'opened'"
              class="!w-2 !h-2 rounded-full bg-red-500"
            ></div>
            <div v-else class="!w-2 !h-2 rounded-full bg-green-500"></div>
          </div>
        </div>
        <div
          class="border border-surface bg-surface-100 p-2 rounded-lg text-sm whitespace-pre-line dark:bg-surface-800"
        >
          {{ object.content }}
        </div>
      </div>
    </div>
  </div>
</template>
